import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import { parseYYYYMMDD } from '@webapp/core/utils/dates'
import {
  SpendingCard,
  type SpendingCardConstructor,
} from '@webapp/modules/spending-cards'
import { Account, type AccountConstructor } from '@webapp/platform/finance'
import { type ExpenseTypeData } from './ExpenseTypeData'
import { type ExpensePaymentTypeEnum } from '../enums/ExpensePaymentTypeEnum'
import { type ExpenseTypeEnum } from '../enums/ExpenseTypeEnum'

export interface ExpenseItemConstructor {
  id: number
  attachment: Nullable<string>
  attachmentMimetype: Nullable<string>
  merchant: string
  account: AccountConstructor
  transactionDate: string
  currency: CurrencyConstructor
  reimbursable: boolean
  amount: number
  taxAmount: number
  taxPercentage: string
  taxName: string
  taxId: Nullable<number>
  paymentCard: Nullable<SpendingCardConstructor>
  notes: string
  expenseType: ExpenseTypeEnum
  paymentType: ExpensePaymentTypeEnum
  expenseTypeData: ExpenseTypeData
  mileageRatePresetId: Nullable<number>
  mileageRatePresetName: Nullable<string>
}

export class ExpenseItem {
  readonly id: number
  readonly attachment: Nullable<string>
  readonly attachmentMimetype: Nullable<string>
  readonly merchant: string
  readonly account: Account
  readonly transactionDate: Date
  readonly currency: Currency
  readonly reimbursable: boolean
  readonly amount: number
  readonly taxAmount: number
  readonly taxPercentage: string
  readonly taxName: string
  readonly taxId: Nullable<number>
  readonly paymentCard: Nullable<SpendingCard>
  readonly notes: string
  readonly expenseType: ExpenseTypeEnum
  readonly paymentType: ExpensePaymentTypeEnum
  readonly expenseTypeData: ExpenseTypeData
  readonly mileageRatePresetId: Nullable<number>
  readonly mileageRatePresetName: Nullable<string>

  constructor(data: ExpenseItemConstructor) {
    this.id = data.id
    this.attachment = data.attachment
    this.attachmentMimetype = data.attachmentMimetype
    this.merchant = data.merchant
    this.account = new Account(data.account)
    this.transactionDate = parseYYYYMMDD(data.transactionDate.slice(0, 10))
    this.currency = new Currency(data.currency)
    this.reimbursable = data.reimbursable
    this.amount = data.amount
    this.taxAmount = data.taxAmount
    this.taxPercentage = data.taxPercentage
    this.taxName = data.taxName
    this.taxId = data.taxId ?? null
    this.paymentCard = data.paymentCard
      ? new SpendingCard(data.paymentCard)
      : null
    this.notes = data.notes || ''
    this.expenseType = data.expenseType
    this.paymentType = data.paymentType
    this.expenseTypeData = data.expenseTypeData
    this.mileageRatePresetId = data.mileageRatePresetId
    this.mileageRatePresetName = data.mileageRatePresetName
  }

  get hasAttachment(): boolean {
    return this.attachment !== null
  }

  get hasNotes(): boolean {
    return this.notes.length > 0
  }

  get hasPaymentCard(): boolean {
    return this.paymentCard !== null
  }

  get subtotal(): number {
    const base = this.amount - this.taxAmount
    if (this.expenseTypeData.type === 'MEALS') {
      return base - this.expenseTypeData.data.tips
    }
    return base
  }
}
